import { useEffect, useState } from "react";
import { api } from "../../../api/client.js";
import type { ImageBundleView } from "../../../api/types.js";
import { bundleDownloadHref } from "./image-ui.js";
import { TelegramPublishButton } from "./TelegramPublish.js";

/**
 * The Bundles saved from ONE Image request, shown in its HISTORY detail so they can be found and downloaded without going back to
 * CREATE / IMAGE. Read-only reuse of what already exists: the `GET /images/:tenantId/bundles` list (filtered here to this
 * request's `batchId`), the same Bundle records, and the same `.../bundles/:id/download` archive behind "Download all". It creates nothing.
 *
 * Degrades quietly: if the Bundle list cannot be loaded, this section says so and the rest of the request detail is unaffected.
 * `refreshKey` lets the page reload the list after the user saves a new Bundle from this same page.
 */
export function ImageRequestBundles({ tenantId, batchId, refreshKey = 0, canPublishToTelegram = false, csrfToken = null }: { readonly tenantId: string; readonly batchId: string; readonly refreshKey?: number; readonly canPublishToTelegram?: boolean; readonly csrfToken?: string | null }) {
  const [bundles, setBundles] = useState<ImageBundleView[] | null>(null);
  const [unavailable, setUnavailable] = useState(false);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ bundles: ImageBundleView[] }>(`/images/${encodeURIComponent(tenantId)}/bundles`)
      .then((result) => {
        if (cancelled) return;
        setBundles(result.bundles.filter((bundle) => bundle.batchId === batchId));
        setUnavailable(false);
      })
      .catch(() => {
        if (!cancelled) setUnavailable(true);
      });
    return () => {
      cancelled = true;
    };
  }, [tenantId, batchId, refreshKey]);

  return (
    <div className="card" data-testid="image-request-bundles">
      <h3>Bundles</h3>
      {bundles === null ? (
        <p className="muted">{unavailable ? "Bundles are unavailable right now." : "Loading…"}</p>
      ) : bundles.length === 0 ? (
        <p className="muted" data-testid="image-request-bundles-empty">
          No bundles created for this request.
        </p>
      ) : (
        <div className="grid" style={{ gap: 12 }}>
          {bundles.map((bundle) => (
            <div key={bundle.id} data-testid={`image-request-bundle-${bundle.id}`}>
              <div>
                <strong>{new Date(bundle.createdAt).toLocaleString()}</strong> — {bundle.members.length} image{bundle.members.length === 1 ? "" : "s"} —{" "}
                <a href={bundleDownloadHref(tenantId, bundle.id)} download data-testid={`image-request-bundle-download-all-${bundle.id}`}>
                  Download all
                </a>
                {canPublishToTelegram && (
                  <>
                    {" "}— <TelegramPublishButton tenantId={tenantId} sourceKind="bundle" sourceId={bundle.id} csrfToken={csrfToken} />
                  </>
                )}
              </div>
              <div className="image-bundle-strip">
                {bundle.members.map((member) => (
                  <img key={member.attemptId} src={member.imageUrl} alt="Bundle member" loading="lazy" />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
